/**
 * File: draftingPage.js
 * Origin: isotect
 * Description: [TODO: Description]
 *
 * Version: v3.1.2
 * Created: 2025-12-04
 * Last Updated: 2026-02-25
 *
 * Used By: isotect
 * Maintainer: Zachary Dicks
 *
 * Notes:
 * - Changes in this file may affect all consuming frontend modules.
 * - See Git repository 'isotect' for full change history.
 */

// Icons
import { FaBarsProgress } from "react-icons/fa6";
import { FaClock } from "react-icons/fa6";
import { FaFileContract } from "react-icons/fa6";
import { FaMoneyBill } from "react-icons/fa6";
import { IoMdCheckmark } from "react-icons/io";

// Drafting Page
export const draftingPage = {
    meta: {
        id: "drafting-page",
    },
    hero: {
        image: "../svgs/drafting_hero_apartment-isometric.svg",
        headline: "Professional MEP Drafting Services",
        subheadline: "Local, experienced drafters — no outsourcing overseas.",
        primaryButton: {
            text: "Get Started",
            type: "primary",
        },
        secondaryButton: {
            text: "Get Estimate",
            type: "secondary",
        },
    },
    planCard: {
        title: "Drafting Packages",
        cardType: "package",
        plans: [
            {
                id: "plan-df-basic",
                header: "Basic",
                description:
                    "Great for small updates and quick drafting tasks.",
                buttonText: "Compare Packages",
                buttonLink: "#comparison-table",
            },
            {
                id: "plan-df-standard",
                header: "Standard",
                description:
                    "Perfect for mid-sized projects and multi-discipline coordination.",
                buttonText: "Compare Packages",
                buttonLink: "#comparison-table",
            },
            {
                id: "plan-df-premium",
                header: "Premium",
                description:
                    "Designed for full-scale BIM projects with comprehensive support.",
                buttonText: "Compare Packages",
                buttonLink: "#comparison-table",
            },
        ],
        features: [
            {
                id: "feature-df-1",
                icon: <FaMoneyBill />,
                text: "Flexible Pricing",
            },
            {
                id: "feature-df-2",
                icon: <FaFileContract />,
                text: "No Long-Term Contracts",
            },
            {
                id: "feature-df-3",
                icon: <FaBarsProgress />,
                text: "Regular Project Updates",
            },
            {
                id: "feature-df-4",
                icon: <FaClock />,
                text: "Start Within 24-48 Hours",
            },
        ],
    },
    articles: [
        {
            id: "article-df-1",
            reverse: false,
            image: "../svgs/drafting_setup_building-views.svg",
            headline: "Project Setup & Standards",
            description:
                "We set up project views, sheets, and templates based on your drafting standards.",
        },
        {
            id: "article-df-2",
            reverse: true,
            image: "../svgs/drafting_layouts_mechanical-floor-plan.svg",
            headline: "Accurate MEP System Layouts",
            description:
                "Mechanical, plumbing, and electrical system layouts based on your markups, sketches, and engineering intent.",
        },
        {
            id: "article-df-3",
            reverse: false,
            image: "../svgs/drafting_bim_exploded-building-isometric.svg",
            headline: "Full-Scale BIM Coordination",
            description:
                "Complete BIM coordination across mechanical, plumbing, and electrical systems to minimize clashes, reduce RFIs, and keep construction moving smoothly.",
        },
    ],
    comparisonTable: {
        transpose: true,
        transposeHeader: ["Features", "Basic", "Standard", "Premium"],
        object: [
            {
                "Project Setup & Standards": <IoMdCheckmark />,
                "Drafting from Markups": <IoMdCheckmark />,
                "Multi-Discipline Coordination": "—",
                "BIM Clash Awareness": "—",
                "Engineer Review": "—",
                "Code Compliance Review": "—",
                "QA/QC Checklist": "—",
                "Construction Support": "—",
                "RFI / Submittal Support": "—",
                "Accelerated Turnaround": "—",
                "Dedicated Project Manager": "—",
                "Revision Cycles Included": "1",
                "Sheets Drafted or Modified": "Up to 5",
            },
            {
                "Project Setup & Standards": <IoMdCheckmark />,
                "Drafting from Markups": <IoMdCheckmark />,
                "Multi-Discipline Coordination": <IoMdCheckmark />,
                "BIM Clash Awareness": <IoMdCheckmark />,
                "Engineer Review": <IoMdCheckmark />,
                "Code Compliance Review": <IoMdCheckmark />,
                "QA/QC Checklist": <IoMdCheckmark />,
                "Construction Support": "—",
                "RFI / Submittal Support": "—",
                "Accelerated Turnaround": "—",
                "Dedicated Project Manager": "—",
                "Revision Cycles Included": "2",
                "Sheets Drafted or Modified": "Up to 12",
            },
            {
                "Project Setup & Standards": <IoMdCheckmark />,
                "Drafting from Markups": <IoMdCheckmark />,
                "Multi-Discipline Coordination": <IoMdCheckmark />,
                "BIM Clash Awareness": <IoMdCheckmark />,
                "Engineer Review": <IoMdCheckmark />,
                "Code Compliance Review": <IoMdCheckmark />,
                "QA/QC Checklist": <IoMdCheckmark />,
                "Construction Support": <IoMdCheckmark />,
                "RFI / Submittal Support": <IoMdCheckmark />,
                "Accelerated Turnaround": <IoMdCheckmark />,
                "Dedicated Project Manager": <IoMdCheckmark />,
                "Revision Cycles Included": "3–4",
                "Sheets Drafted or Modified": "Up to 25+",
            },
        ],
    },
    faq: [
        {
            id: "faq-df-1",
            header: "What does the drafting process look like?",
            content:
                "Once you submit a drafting request, our team reviews the scope, available files, and required turnaround. After approval, a project manager is assigned and a drafter begins work on your deliverables. When drafting is complete, all files are returned for review, and the project is billed based on actual hours worked.",
        },
        {
            id: "faq-df-2",
            header: "How far in advance should we schedule drafting work?",
            content:
                "We recommend scheduling drafting at least one week before major project deadlines. Most drafting tasks are completed within one to three business days after the work order is approved, depending on complexity and workload.",
        },
        {
            id: "faq-df-3",
            header: "Are drafting revisions included in the original cost?",
            content:
                "Revisions that require additional drafting work are billed at the standard hourly rate. To minimize rework, we recommend providing clear markups, design intent, and reference materials when submitting your request.",
        },
        {
            id: "faq-df-4",
            header: "What CAD and BIM programs do you use?",
            content:
                "Our drafting team primarily works in Autodesk Revit for 3D modeling and coordinated MEP documentation. Please provide all required Revit models, links, and supporting files to streamline production.",
        },
        {
            id: "faq-df-5",
            header: "Can you follow our internal drafting standards?",
            content:
                "Yes. We can draft according to your company’s standards, templates, and SOPs. We request your drafting guidelines during your first work order so that all future deliverables are aligned with your internal practices.",
        },
        {
            id: "faq-df-6",
            header: "Does drafting include engineering review or QAQC?",
            content:
                "Yes. All drafting work undergoes a quality review by licensed Professional Engineers to ensure accuracy, constructability, and alignment with applicable codes. Basic engineering oversight is included, while more in-depth design reviews can be requested as an additional service.",
        },
    ],
    cta: {
        label: "Explore Drafting Services",
        headline: "Upgrade Your Drafting Workflow",
        description:
            "Reduce design errors, accelerate production, and get permit-ready drawings with confidence.",
    },
};
